import { createContext } from 'react';

/**
 * Discord Activity context.
 *
 * Provided at the top of App.js by <DiscordProvider>. The default
 * value below is what the open-web experience sees — isEmbedded
 * false, everything else null. Components can branch on isEmbedded
 * to add Discord-specific affordances when relevant, without
 * changing behavior for browser users.
 */
export const DiscordContext = createContext({
    isEmbedded: false,
    isReady: false,
    sdk: null,
    channelId: null,
    guildId: null,
    instanceId: null,
    discordUserId: null,
    // The Discord @handle (SocialAccount.provider_username), sourced
    // from the backend `discord_username` field — NOT the synthetic
    // discord_<snowflake> Django username. Safe to render as a name.
    discordUsername: null,
    discordAvatarHash: null,
    // Discord's global display name (separate from the @handle in
    // discordUsername) and the per-server nickname for the guild this
    // Activity launched from. Both come back on the
    // /api/session_auth/discord/ response. Either may be null when the
    // user has none set, when the guild_id is absent (open web), or
    // when the guilds.members.read fetch fails server-side.
    discordGlobalName: null,
    discordNick: null,
    // Two-axis auth state — the iframe can be signed-in to HSH (HTTP
    // session cookie established via /api/session_auth/discord/) while
    // the embedded SDK has not yet completed its internal authenticate()
    // handshake (or vice-versa). Callers branching on Discord identity
    // should check `isAuthenticatedHsh`; callers reaching for SDK-
    // privileged commands (invite dialog, rich presence) should check
    // `isAuthenticatedSdk`.
    isAuthenticatedHsh: false,
    isAuthenticatedSdk: false,
    error: null,
    // Populated by DiscordProvider after the /api/lobby/from_platform/
    // POST completes — the Lobby payload (`{ uuid, seats, ... }`) the
    // backend assigned to this channel binding. New mounters read this
    // directly off context rather than racing the one-shot
    // `discord-lobby-joined` window event.
    lobby: null,
    // is_owner from /api/lobby/from_platform/ — true when this HSH
    // session created or owns the channel-bound Lobby; false when
    // from_platform seated us into a Lobby someone else owns. Used by
    // DiscordLobbyNavigator to decide whether to land on the Main Menu
    // (owner / self-launch) or the Lobby (joiner / peer-bound).
    lobbyIsOwner: null,
    // seat_index from /api/lobby/from_platform/ — non-null integer
    // when seated, null when the lobby was full at find-or-create
    // time. Navigator uses non-null as the "joiner has a real seat"
    // signal.
    lobbySeatIndex: null,
    // game_id from a `{ action: 'rejoin', game_id }` from_platform
    // response — set when the backend routed this peer straight back
    // into an in-progress game in the channel (they already hold a
    // seat) rather than into a forming lobby. DiscordProvider also
    // dispatches the WS Resume; this field is captured for diagnostics
    // (boot panel / Eruda) and stays null on the normal join path.
    rejoinGameId: null,
    // CC_DISCORD_MAIN_MENU (2026-05-30): the boot fetch became a
    // read-only GET of the channel-bound lobbies list (it no longer
    // POSTs or auto-seats anyone). `channelLobbies` is that list — each
    // entry is the backend list row `{ lobby_id, is_owner,
    // current_user_seat_index, lobby }` decorated with two frontend
    // derivations: `displayState` ('forming' | 'in_progress') and
    // `userOwnsSeat` (bool). MainMenu's "Live in this channel" section
    // renders one row per entry under the isEmbedded gate. Starts empty.
    channelLobbies: [],
    // True once the boot from_platform GET resolves (success OR
    // failure). MainMenu uses it to know the Live section is settled,
    // and DiscordLobbyNavigator uses its false→true transition as the
    // cue to land the user on the Main Menu ('/').
    channelLobbiesFetched: false,
    // Per-leg trace of the embedded cold-start handshake, surfaced by
    // DiscordBootPanel as a live status panel inside the Discord
    // Activity iframe. Seven stages, in the order DiscordProvider runs
    // them on embedded mount:
    //
    //   embeddedDetect   — isLikelyEmbedded() (sync, immediate)
    //   sdkReady         — new DiscordSDK() + await sdk.ready()
    //   authorize        — sdk.commands.authorize() → OAuth code
    //   csrfPrime        — GET /api/session_auth/csrf/
    //   hshSession       — POST /api/session_auth/discord/
    //   sdkAuthenticate  — sdk.commands.authenticate()
    //   lobbyFetch       — GET /api/lobby/from_platform/
    //
    // Each stage is { status, startedAt, completedAt, error }. Status
    // is the state machine 'pending' → 'ok' | 'failed'; startedAt /
    // completedAt are Date.now() epoch ms (the panel renders their
    // delta as elapsed time); error is the same token DiscordProvider
    // writes to `error` for that leg (authorize-failed,
    // backend-<status>, sdk-authenticate-failed, missing-app-id, …).
    // On open web every stage stays 'pending' with null timestamps and
    // the panel renders nothing (it short-circuits on isEmbedded).
    bootStages: {
        embeddedDetect:
            { status: 'pending', startedAt: null, completedAt: null, error: null },
        sdkReady:
            { status: 'pending', startedAt: null, completedAt: null, error: null },
        authorize:
            { status: 'pending', startedAt: null, completedAt: null, error: null },
        csrfPrime:
            { status: 'pending', startedAt: null, completedAt: null, error: null },
        hshSession:
            { status: 'pending', startedAt: null, completedAt: null, error: null },
        sdkAuthenticate:
            { status: 'pending', startedAt: null, completedAt: null, error: null },
        lobbyFetch:
            { status: 'pending', startedAt: null, completedAt: null, error: null },
    },
});

